import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { formatDistanceToNow, format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { FileText, Clock, Check, X, DollarSign, Send, Calendar, CreditCard, CheckCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEPOSIT_PERCENTAGE = 0.30;

const tattooStyles = [
  'Tradicional', 'Neo-Tradicional', 'Realismo', 'Blackwork',
  'Dotwork', 'Japonés', 'Tribal', 'Minimalista',
  'Acuarela', 'Geométrico', 'Chicano', 'Lettering'
];

const Quotes = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const dateLocale = language === 'es' ? es : enUS;

  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [respondDialogOpen, setRespondDialogOpen] = useState(false);
  const [responseData, setResponseData] = useState({
    price_min: '',
    price_max: '',
    estimated_sessions: 1,
    estimated_hours: 1,
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    fetchQuotes();

    // Check for payment status in URL params
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');

    if (sessionId && status === 'success') {
      checkPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      toast.error(language === 'es' ? 'Pago cancelado' : 'Payment cancelled');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, language]);

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/deposits/status/${sessionId}`);
      if (response.data.status === 'paid') {
        toast.success(language === 'es' ? '¡Depósito pagado exitosamente!' : 'Deposit paid successfully!');
        fetchQuotes();
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes`);
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async () => {
    if (!responseData.price_min || !responseData.price_max) {
      toast.error(language === 'es' ? 'Ingresa el rango de precios' : 'Enter the price range');
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`${API}/quotes/${selectedQuote.id}/respond`, {
        price_min: parseInt(responseData.price_min),
        price_max: parseInt(responseData.price_max),
        estimated_sessions: responseData.estimated_sessions,
        estimated_hours: parseFloat(responseData.estimated_hours),
        message: responseData.message
      });

      await fetchQuotes();
      setRespondDialogOpen(false);
      setSelectedQuote(null);
      setResponseData({ price_min: '', price_max: '', estimated_sessions: 1, estimated_hours: 1, message: '' });
      toast.success(language === 'es' ? '¡Cotización enviada!' : 'Quote sent!');
    } catch (error) {
      console.error('Error responding to quote:', error);
      toast.error(language === 'es' ? 'Error al enviar cotización' : 'Error sending quote');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayDeposit = async (quoteId) => {
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API}/deposits/create`, {
        quote_id: quoteId,
        origin_url: window.location.origin
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error creating deposit:', error);
      toast.error(language === 'es' ? 'Error al procesar el pago' : 'Error processing payment');
      setProcessingPayment(false);
    }
  };

  const handleAcceptQuote = async (quoteId) => {
    try {
      await axios.put(`${API}/quotes/${quoteId}/accept`);
      await fetchQuotes();
      toast.success(language === 'es' ? '¡Cotización aceptada!' : 'Quote accepted!');
    } catch (error) {
      console.error('Error accepting quote:', error);
      toast.error(language === 'es' ? 'Error al aceptar' : 'Error accepting');
    }
  };

  const handleRejectQuote = async (quoteId) => {
    try {
      await axios.put(`${API}/quotes/${quoteId}/reject`);
      await fetchQuotes();
      toast.success(language === 'es' ? 'Cotización rechazada' : 'Quote rejected');
    } catch (error) {
      console.error('Error rejecting quote:', error);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'text-yellow-400 bg-yellow-900/30', label: language === 'es' ? 'Pendiente' : 'Pending' },
      responded: { color: 'text-blue-400 bg-blue-900/30', label: language === 'es' ? 'Respondida' : 'Responded' },
      accepted: { color: 'text-emerald-400 bg-emerald-900/30', label: language === 'es' ? 'Aceptada' : 'Accepted' },
      rejected: { color: 'text-rose-400 bg-rose-900/30', label: language === 'es' ? 'Rechazada' : 'Rejected' },
    };
    const badge = badges[status] || badges.pending;
    return <span className={`text-xs uppercase tracking-wider px-2 py-1 ${badge.color}`}>{badge.label}</span>;
  };

  const pendingQuotes = quotes.filter(q => q.status === 'pending');
  const respondedQuotes = quotes.filter(q => q.status === 'responded');
  const otherQuotes = quotes.filter(q => ['accepted', 'rejected'].includes(q.status));

  // Artist view
  if (user?.user_type === 'artist') {
    return (
      <div className="max-w-4xl mx-auto px-3 sm:px-4 py-6 sm:py-8" data-testid="quotes-container">
        <div className="mb-8">
          <h1 className="font-['Cinzel'] text-3xl font-bold text-white">{t('quotes.title')}</h1>
          <p className="text-zinc-500 mt-1">{t('quotes.subtitle')}</p>
        </div>

        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger value="pending" className="data-[state=active]:bg-zinc-800">
              <Clock size={16} className="mr-2" />
              {t('quotes.pendingQuotes')} ({pendingQuotes.length})
            </TabsTrigger>
            <TabsTrigger value="responded" className="data-[state=active]:bg-zinc-800">
              <Check size={16} className="mr-2" />
              {t('quotes.respondedQuotes')} ({respondedQuotes.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-6">
            {loading ? (
              <div className="space-y-4">
                {[...Array(2)].map((_, i) => (
                  <div key={i} className="h-40 bg-zinc-900 animate-pulse" />
                ))}
              </div>
            ) : pendingQuotes.length === 0 ? (
              <div className="text-center py-16 bg-zinc-900/30 border border-zinc-800">
                <FileText className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500">{t('quotes.noQuotes')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingQuotes.map((quote) => (
                  <QuoteCard
                    key={quote.id}
                    quote={quote}
                    isArtist={true}
                    onRespond={() => {
                      setSelectedQuote(quote);
                      setRespondDialogOpen(true);
                    }}
                    onReject={() => handleRejectQuote(quote.id)}
                    t={t}
                    dateLocale={dateLocale}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="responded" className="mt-6">
            {[...respondedQuotes, ...otherQuotes].length === 0 ? (
              <div className="text-center py-16 bg-zinc-900/30 border border-zinc-800">
                <p className="text-zinc-500">{t('quotes.noQuotes')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {[...respondedQuotes, ...otherQuotes].map((quote) => (
                  <QuoteCard
                    key={quote.id}
                    quote={quote}
                    isArtist={true}
                    t={t}
                    dateLocale={dateLocale}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Respond Dialog */}
        <Dialog open={respondDialogOpen} onOpenChange={setRespondDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-['Cinzel'] text-xl">{t('quotes.respondToQuote')}</DialogTitle>
            </DialogHeader>
            {selectedQuote && (
              <div className="space-y-4 mt-4">
                <div className="bg-zinc-800/50 p-4 rounded text-sm">
                  <p className="text-zinc-400 mb-2"><strong>{t('quotes.describeIdea')}:</strong> {selectedQuote.description}</p>
                  {selectedQuote.body_part && <p className="text-zinc-500">{t('quotes.bodyPart')}: {selectedQuote.body_part}</p>}
                  {selectedQuote.size && <p className="text-zinc-500">{t('quotes.approximateSize')}: {selectedQuote.size}</p>}
                  {selectedQuote.style && <p className="text-zinc-500">{t('quotes.preferredStyle')}: {selectedQuote.style}</p>}
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">{t('quotes.priceRange')} *</Label>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
                      <Input
                        type="number"
                        value={responseData.price_min}
                        onChange={(e) => setResponseData(prev => ({ ...prev, price_min: e.target.value }))}
                        className="bg-transparent border-zinc-800 pl-9"
                        placeholder={t('quotes.minPrice')}
                        data-testid="price-min"
                      />
                    </div>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
                      <Input
                        type="number"
                        value={responseData.price_max}
                        onChange={(e) => setResponseData(prev => ({ ...prev, price_max: e.target.value }))}
                        className="bg-transparent border-zinc-800 pl-9"
                        placeholder={t('quotes.maxPrice')}
                        data-testid="price-max"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-zinc-500">{t('quotes.estimatedSessions')}</Label>
                    <Input
                      type="number"
                      min="1"
                      value={responseData.estimated_sessions}
                      onChange={(e) => setResponseData(prev => ({ ...prev, estimated_sessions: parseInt(e.target.value) || 1 }))}
                      className="bg-transparent border-zinc-800 mt-2"
                      data-testid="sessions"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-zinc-500">{t('quotes.estimatedHours')}</Label>
                    <Input
                      type="number"
                      min="0.5"
                      step="0.5"
                      value={responseData.estimated_hours}
                      onChange={(e) => setResponseData(prev => ({ ...prev, estimated_hours: parseFloat(e.target.value) || 1 }))}
                      className="bg-transparent border-zinc-800 mt-2"
                      data-testid="hours"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">{t('quotes.messageToClient')}</Label>
                  <Textarea
                    value={responseData.message}
                    onChange={(e) => setResponseData(prev => ({ ...prev, message: e.target.value }))}
                    className="bg-transparent border-zinc-800 mt-2 min-h-[80px]"
                    placeholder={t('quotes.messagePlaceholder')}
                    data-testid="quote-message"
                  />
                </div>

                <Button
                  onClick={handleRespond}
                  disabled={submitting}
                  className="w-full bg-zinc-100 text-zinc-950 hover:bg-white uppercase tracking-widest text-xs font-bold py-4"
                  data-testid="send-quote-btn"
                >
                  {submitting ? '...' : t('quotes.sendQuote')}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Client view
  return (
    <div className="max-w-4xl mx-auto px-3 sm:px-4 py-6 sm:py-8" data-testid="quotes-container">
      <div className="mb-8">
        <h1 className="font-['Cinzel'] text-3xl font-bold text-white">{t('quotes.myQuotes')}</h1>
        <p className="text-zinc-500 mt-1">{t('quotes.subtitle')}</p>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="h-40 bg-zinc-900 animate-pulse" />
          ))}
        </div>
      ) : quotes.length === 0 ? (
        <div className="text-center py-16 bg-zinc-900/30 border border-zinc-800">
          <FileText className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500">{t('quotes.noQuotes')}</p>
          <p className="text-sm text-zinc-600 mt-2">
            {language === 'es'
              ? 'Visita el perfil de un artista para solicitar una cotización'
              : "Visit an artist's profile to request a quote"}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {quotes.map((quote) => (
            <QuoteCard
              key={quote.id}
              quote={quote}
              isArtist={false}
              onAccept={() => handleAcceptQuote(quote.id)}
              onReject={() => handleRejectQuote(quote.id)}
              onPayDeposit={() => handlePayDeposit(quote.id)}
              processingPayment={processingPayment}
              t={t}
              dateLocale={dateLocale}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const QuoteCard = ({ quote, isArtist, onRespond, onAccept, onReject, onPayDeposit, processingPayment, t, dateLocale }) => {
  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'text-yellow-400 bg-yellow-900/30', label: t('appointments.pending') },
      responded: { color: 'text-blue-400 bg-blue-900/30', label: t('quotes.respondedQuotes').split(' ')[0] },
      accepted: { color: 'text-emerald-400 bg-emerald-900/30', label: t('appointments.accepted') },
      rejected: { color: 'text-rose-400 bg-rose-900/30', label: t('appointments.rejected') },
      deposit_paid: { color: 'text-emerald-400 bg-emerald-900/30', label: 'Depósito Pagado' },
    };
    const badge = badges[status] || badges.pending;
    return <span className={`text-xs uppercase tracking-wider px-2 py-1 ${badge.color}`}>{badge.label}</span>;
  };

  const depositAmount = quote.price_min ? (quote.price_min * DEPOSIT_PERCENTAGE).toFixed(2) : 0;

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 p-4 sm:p-6" data-testid={`quote-${quote.id}`}>
      <div className="flex items-start gap-4">
        <Avatar className="h-12 w-12 border border-zinc-800">
          <AvatarImage src={isArtist ? quote.client_avatar : quote.artist_avatar} />
          <AvatarFallback className="bg-zinc-800 text-zinc-400">
            {(isArtist ? quote.client_name : quote.artist_name)?.charAt(0)?.toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-2 mb-2">
            <div>
              <p className="font-medium text-white">{isArtist ? quote.client_name : quote.artist_name}</p>
              <p className="text-xs text-zinc-600">
                {formatDistanceToNow(new Date(quote.created_at), { addSuffix: true, locale: dateLocale })}
              </p>
            </div>
            {getStatusBadge(quote.status)}
          </div>

          <p className="text-zinc-400 mb-3">{quote.description}</p>

          <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-zinc-500 mb-4">
            {quote.body_part && <span>{t('quotes.bodyPart')}: {quote.body_part}</span>}
            {quote.size && <span>{t('quotes.approximateSize')}: {quote.size}</span>}
            {quote.style && <span>{t('quotes.preferredStyle')}: {quote.style}</span>}
          </div>

          {/* Show quote response if responded */}
          {quote.status !== 'pending' && quote.price_min && (
            <div className={`bg-zinc-800/50 p-4 mt-4 border-l-2 ${quote.deposit_paid ? 'border-emerald-500' : 'border-blue-500'}`}>
              <p className="text-sm text-zinc-400 mb-2">{t('quotes.priceLabel')}:</p>
              <p className="text-2xl font-mono text-white mb-2">
                ${quote.price_min} - ${quote.price_max}
              </p>
              <div className="flex gap-4 text-sm text-zinc-500">
                <span>{t('quotes.sessionsLabel')}: {quote.estimated_sessions}</span>
                <span>{t('quotes.hoursLabel')}: {quote.estimated_hours}h</span>
              </div>
              {quote.artist_message && (
                <p className="text-sm text-zinc-400 mt-3 pt-3 border-t border-zinc-700">
                  {quote.artist_message}
                </p>
              )}

              {/* Deposit info */}
              {quote.status === 'responded' && !quote.deposit_paid && !isArtist && (
                <div className="mt-4 pt-4 border-t border-zinc-700">
                  <p className="text-sm text-zinc-400 mb-2">Depósito requerido (30%):</p>
                  <p className="text-xl font-mono text-emerald-400">${depositAmount}</p>
                </div>
              )}

              {quote.deposit_paid && (
                <div className="mt-4 pt-4 border-t border-zinc-700 flex items-center gap-2 text-emerald-400">
                  <CheckCircle size={18} />
                  <span className="text-sm">Depósito pagado: ${quote.deposit_amount?.toFixed(2)}</span>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          {quote.status === 'pending' && isArtist && (
            <div className="flex flex-wrap gap-2 sm:gap-3 mt-4">
              <Button
                onClick={onRespond}
                className="bg-zinc-100 text-zinc-950 hover:bg-white uppercase tracking-widest text-xs font-bold"
                data-testid={`respond-quote-${quote.id}`}
              >
                {t('appointments.respond')}
              </Button>
              <Button
                onClick={onReject}
                variant="outline"
                className="border-zinc-700 text-zinc-400 hover:text-white uppercase tracking-widest text-xs"
                data-testid={`reject-quote-${quote.id}`}
              >
                {t('appointments.reject')}
              </Button>
            </div>
          )}

          {quote.status === 'responded' && !isArtist && !quote.deposit_paid && (
            <div className="flex flex-wrap gap-2 sm:gap-3 mt-4">
              <Button
                onClick={onPayDeposit}
                disabled={processingPayment}
                className="bg-emerald-600 hover:bg-emerald-500 text-white uppercase tracking-widest text-xs font-bold"
                data-testid={`pay-deposit-${quote.id}`}
              >
                {processingPayment ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <CreditCard size={16} className="mr-2" />
                    Pagar Depósito (${(quote.price_min * DEPOSIT_PERCENTAGE).toFixed(2)})
                  </>
                )}
              </Button>
              <Button
                onClick={onReject}
                variant="outline"
                className="border-rose-800 text-rose-400 hover:bg-rose-900/20 uppercase tracking-widest text-xs"
                data-testid={`reject-quote-${quote.id}`}
              >
                <X size={16} className="mr-2" />
                {t('quotes.rejectQuote')}
              </Button>
            </div>
          )}

          {quote.status === 'deposit_paid' && !isArtist && (
            <div className="flex items-center gap-2 mt-4 text-emerald-400">
              <CheckCircle size={18} />
              <span className="text-sm font-medium">¡Depósito confirmado! El artista se pondrá en contacto contigo.</span>
            </div>
          )}

          {quote.status === 'pending' && !isArtist && (
            <p className="text-sm text-zinc-500 mt-4 italic">{t('quotes.waitingResponse')}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Quotes;
